import { Link } from "react-router-dom";
import type { Schedule } from "../types";

interface SchedulePageHeaderProps {
  schedule: Schedule;
  syncedText: string;
  onRefresh: () => void;
  isRefreshing: boolean;
  /**
   * Optional toolbar slot rendered to the right of Refresh. Used by the
   * schedule home page to mount admin-only actions (Rooms, Subjects, …) without
   * coupling this header to per-action role checks.
   */
  toolbarSlot?: React.ReactNode;
  /**
   * Optional right-aligned slot rendered at the trailing edge of the header.
   * Distinct from {@link toolbarSlot} so the Run Scheduler button stays pinned
   * to the right regardless of how many admin actions render — and so it
   * surfaces for non-admins too. The slot itself controls its own `ml-auto`
   * margin; the header only provides positional space.
   */
  runSlot?: React.ReactNode;
}

export function SchedulePageHeader({
  schedule,
  syncedText,
  onRefresh,
  isRefreshing,
  toolbarSlot,
  runSlot,
}: SchedulePageHeaderProps) {
  return (
    <header
      data-testid="schedule-page-header"
      className="sticky top-0 z-20 bg-surface border-b border-border-subtle"
    >
      <div className="flex items-center gap-3.5 px-5 py-2.5 flex-wrap">
        <Link
          to="/schedules"
          data-testid="header-back"
          aria-label="Back to schedules"
          className="flex items-center gap-1.5 text-text-muted hover:text-white"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
        </Link>
        <div className="pr-3.5 border-r border-border-subtle">
          <h2
            data-testid="schedule-name"
            className="m-0 text-[15px] font-bold tracking-tight text-white"
          >
            {schedule.name}
          </h2>
          <div
            data-testid="schedule-synced-text"
            className="text-[11px] text-text-faint"
          >
            {syncedText}
          </div>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={isRefreshing}
          data-testid="schedule-refresh"
          aria-label="Refresh calendar"
          className="bg-surface-2 text-text-soft rounded-md px-3 py-1.5 font-medium text-[13px] cursor-pointer flex items-center gap-1.5 hover:bg-surface-2/80 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
            className={isRefreshing ? "animate-spin" : ""}
          >
            <path d="M3 12a9 9 0 0 1 15-6.7L21 8M21 3v5h-5M21 12a9 9 0 0 1-15 6.7L3 16M3 21v-5h5" />
          </svg>
          Refresh Calendar
        </button>
        {toolbarSlot}
        {runSlot}
      </div>
    </header>
  );
}
